// admin-ui/src/api/topic-pages.ts — Track B v1 topic landing client
import { api } from "./client";

export type TopicPageStatus = "draft" | "active" | "retired";
export type HeroKind = "trailer" | "static";

export interface RelatedLink {
  label: string;
  href: string;
  kind: "topic" | "issue" | "article";
}

export interface TopicPageRow {
  id: number;
  publication_slug: string;
  slug: string;
  title: string;
  h1: string | null;
  lede: string | null;
  meta_description: string | null;
  banner_copy: string | null;
  hero_kind: HeroKind;
  hero_article_id: number | null;
  sample_article_ids: number[] | null;
  spread_pick_ids: number[] | null;
  related_links: RelatedLink[] | null;
  status: TopicPageStatus;
  agent_last_run_at: string | null;
  editor_locked_fields: string[];
  created_at: string;
  updated_at: string;
}

export type ProposalStatus = "pending" | "applied" | "rejected" | "reverted";
export type ProposalAutonomy = "auto" | "proposed";

export interface AgentProposalRow {
  id: number;
  agent_run_id: string;
  surface: string;
  target_pk: Record<string, unknown>;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown>;
  autonomy: ProposalAutonomy;
  status: ProposalStatus;
  editor_id: number | null;
  decided_at: string | null;
  rationale: string | null;
  created_at: string;
}

export const topicPagesApi = {
  list: (publication_slug: string, status?: TopicPageStatus) => {
    const p = new URLSearchParams({ publication_slug });
    if (status) p.set("status", status);
    return api.get<{ topic_pages: TopicPageRow[] }>(
      `/api/admin/topic-pages?${p}`,
    );
  },

  get: (publication_slug: string, slug: string) =>
    api.get<TopicPageRow>(
      `/api/admin/topic-pages/${encodeURIComponent(slug)}?publication_slug=${encodeURIComponent(publication_slug)}`,
    ),

  patch: (
    publication_slug: string, slug: string,
    body: Partial<{
      title: string; h1: string; lede: string;
      meta_description: string; banner_copy: string;
      hero_kind: HeroKind; hero_article_id: number;
      sample_article_ids: number[]; spread_pick_ids: number[];
      related_links: RelatedLink[]; status: TopicPageStatus;
    }>,
  ) =>
    api.patch<TopicPageRow>(
      `/api/admin/topic-pages/${encodeURIComponent(slug)}?publication_slug=${encodeURIComponent(publication_slug)}`,
      body,
    ),
};

export const agentProposalsApi = {
  list: (status: ProposalStatus | "all" = "pending", surface?: string) => {
    const p = new URLSearchParams({ status });
    if (surface) p.set("surface", surface);
    return api.get<{ proposals: AgentProposalRow[] }>(
      `/api/admin/agent-proposals?${p}`,
    );
  },

  approve: (id: number) =>
    api.post<ApproveResponse>(`/api/admin/agent-proposals/${id}/approve`, {}),

  reject: (id: number) =>
    api.post<AgentProposalRow>(`/api/admin/agent-proposals/${id}/reject`, {}),

  revert: (id: number) =>
    api.post<AgentProposalRow>(`/api/admin/agent-proposals/${id}/revert`, {}),
};

export interface ApproveResponse {
  proposal: AgentProposalRow;
  spawned_proposal: AgentProposalRow | null;
  spawn_note: string | null;
}
